// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { ago } = require('shared/date')
const {
  ARTICLE_SUBTYPES_KEYS: articleSubtypeKeys
} = require('shared/model-types')
const { formatCount } = require('shared/string-utils')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { eventHandler } = require('client-v2/common/utils/events')
const {
  ArticleAddCircleButton,
  CircleButton
} = require('client-v2/common/components/buttons')
const InlineFrame = require('client-v2/common/components/inline-frame')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { articleDomainSearchHref } = require('client-v2/common/models/article/actions')
const { userProfileHref } = require('client-v2/common/models/user/actions')
// Container imports
const { TabNameEnum } = require('../state/slide-record')
const ArticleProfileCollections = require('./collections-smart')
const ArticleProfileComments = require('./comments-smart')
const ArticleProfileInterests = require('./interests-smart')

// BEM Block entity definition
const bem = BemEntity('ArticleProfileDetailCardV2')

function ArticleProfileDetailCard(props) {
  const { article, slideIndex } = props
  return (
    tag('article', {
      className: classNames(
        props.className,
        bem.block({
          // Prefer the subtype enum key in the stylesheet instead of the value
          subtype: articleSubtypeKeys[article.subtype]
        })
      )
    },
      tag(Curator, props),
      tag('div', {
        className: bem.element('contents')
      },
        tag('div', {
          className: bem.element('main-content')
        },
          tag('div', {
            className: bem.element('article-info')
          },
            tag(ArticleImage, props),
            tag(ArticleActions, props),
            tag(ArticleDomain, props),
            tag(ArticleTitle, props),
            tag(ArticleDescription, props)
          ),

          tag('div', {
            className: bem.element('comments-content')
          },
            tag(ArticleProfileComments, { slideIndex })
          ),

          tag(Tabs, props)
        ),

        tag('div', {
          className: bem.element('sidebar-content')
        },
          tag(ArticleProfileCollections, { slideIndex }),
          tag(ArticleProfileInterests, { slideIndex })
        )
      )
    )
  )
}

const tabPropTypes = {
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired
}

ArticleProfileDetailCard.propTypes = {
  // Flags
  isEmbedDisclosed: PropTypes.bool,
  // Values
  article: PropTypes.object.isRequired,
  className: PropTypes.string,
  curatedAt: PropTypes.string,
  curatedBy: PropTypes.object,
  slideIndex: PropTypes.number,
  tabs: PropTypes.shape({
    [TabNameEnum.COLLECTIONS]: PropTypes.shape(tabPropTypes),
    [TabNameEnum.COMMENTS]: PropTypes.shape(tabPropTypes),
    [TabNameEnum.INTERESTS]: PropTypes.shape(tabPropTypes)
  }).isRequired,
  // Callbacks
  onAddClick: PropTypes.func,
  onContentClick: PropTypes.func,
  onDomainClick: PropTypes.func,
  onMoreActionsClick: PropTypes.func,
  onShareClick: PropTypes.func,
  onTabSelect: PropTypes.func,
  onUserNavigate: PropTypes.func
}

ArticleProfileDetailCard.defaultProps = {
  isEmbedDisclosed: false,
  isCuratorVisible: true
}

module.exports = ArticleProfileDetailCard

// BEM Block entity Element components

function Curator(props) {
  const { curatedAt, curatedBy, isCuratorVisible } = props
  if (!curatedBy || !isCuratorVisible) {
    // Show the empty space where the curator should be
    return tag('div', {
      className: bem.element('curator')
    })
  }
  const onUserClick = eventHandler(props.onUserNavigate, { user: curatedBy })
  return (
    tag('div', {
      className: bem.element('curator')
    },
      tag(UserAvatar, {
        className: bem.element('curator-icon'),
        href: true,
        user: curatedBy,
        onClick: onUserClick
      }),

      'First added by ',
      tag('a', {
        className: bem.element('curator-href'),
        href: userProfileHref(curatedBy),
        onClick: onUserClick
      },
        `@${curatedBy.username}`
      ),

      curatedAt
      ? tag('time', {
        className: bem.element('curator-time'),
        dateTime: curatedAt
      },
        ago(curatedAt)
      )
      : null
    )
  )
}

function ArticleImage(props) {
  const { article } = props
  const { embedUrl, image, md5image, url } = article
  if (!image && !md5image) {
    return null
  }
  // Conditionally render the embedded player based on article subtype
  let onImageClick = eventHandler(props.onContentClick, { article })
  let embeddedPlayer
  if (embedUrl) {
    onImageClick = null
    embeddedPlayer = tag(EmbeddedPlayer, {
      isDisclosed: props.isEmbedDisclosed,
      src: embedUrl
    })
  }

  return (
    tag('div', {
      className: bem.element('image-container')
    },
      tag(LazyImage, {
        className: bem.element('image'),
        href: url,
        rel: 'nofollow noopener',
        target: '_blank',
        sources: [md5image, image],
        onClick: onImageClick
      }),
      embeddedPlayer
    )
  )
}

function EmbeddedPlayer(props) {
  const [isDisclosed, setDisclosed] = React.useState(props.isDisclosed)

  if (!isDisclosed) {
    return tag('div', {
      className: bem.element('embed', 'curtain'),
      onClick: event => {
        event.stopPropagation()
        setDisclosed(true)
      }
    })
  }
  return tag(InlineFrame, {
    className: bem.element('embed', 'player'),
    src: props.src
  })
}

function ArticleActions(props) {
  const { article } = props
  return (
    tag('div', {
      className: bem.element('actions')
    },
      tag(CircleButton, {
        className: bem.element('action-button', 'more'),
        title: 'Show more actions for this article',
        onClick: () => props.onMoreActionsClick({ article })
      }),
      tag(CircleButton, {
        className: bem.element('action-button', 'share'),
        title: 'Share this article with a friend',
        onClick: () => props.onShareClick({ article })
      }),
      tag(ArticleAddCircleButton, {
        className: bem.element('action-button', 'add'),
        checked: props.article.hasMix,
        title: 'Add this article to your collections',
        onClick: () => props.onAddClick({ article })
      }),
      tag('div', {
        className: bem.element('count')
      },
        formatCount(props.article.count)
      )
    )
  )
}

function ArticleDomain(props) {
  const { article } = props
  return (
    tag('a', {
      className: bem.element('domain'),
      href: articleDomainSearchHref(article),
      title: article.hostname,
      onClick: eventHandler(props.onDomainClick, { article })
    },
      article.hostname
    )
  )
}

function ArticleTitle(props) {
  const { article } = props
  return (
    tag('a', {
      className: bem.element('title'),
      href: article.url,
      rel: 'nofollow noopener',
      target: '_blank',
      title: article.title,
      onClick: eventHandler(props.onContentClick, { article })
    },
      article.title
    )
  )
}

function ArticleDescription(props) {
  const { article } = props
  if (!article.description) {
    return null
  }
  return (
    tag('a', {
      className: bem.element('description'),
      href: article.url,
      rel: 'nofollow noopener',
      target: '_blank',
      title: article.description,
      onClick: eventHandler(props.onContentClick, { article })
    },
      article.description
    )
  )
}

function Tabs(props) {
  const { article, slideIndex, tabs } = props

  return (
    tag('div', {
      className: bem.element('tabs-content')
    },
      tag('nav', {
        className: bem.element('nav')
      },
        tag('ul', {
          className: bem.element('tabs')
        },
          renderTab(TabNameEnum.COLLECTIONS),
          renderTab(TabNameEnum.COMMENTS),
          renderTab(TabNameEnum.INTERESTS)
        )
      ),
      renderSelectedTab()
    )
  )

  function onSelect(selectedTab) {
    props.onTabSelect({ article, selectedTab })
  }

  function renderTab(name) {
    return tag(Tab, {
      ...tabs[name],
      name: name,
      onSelect
    })
  }

  function renderSelectedTab() {
    if (tabs[TabNameEnum.COLLECTIONS].isSelected) {
      return tag(ArticleProfileCollections, { slideIndex })
    }
    if (tabs[TabNameEnum.COMMENTS].isSelected) {
      return tag(ArticleProfileComments, { slideIndex })
    }
    if (tabs[TabNameEnum.INTERESTS].isSelected) {
      return tag(ArticleProfileInterests, { slideIndex })
    }
  }
}

function Tab(props) {
  return (
    tag('li', {
      className: bem.element('tab', {
        [props.name]: true,
        loading: props.isLoading,
        selected: props.isSelected
      }),
      title: props.label,
      onClick: () => props.onSelect(props.name)
    },
      props.label
    )
  )
}
